import React, { useState, createContext, useMemo, useContext } from 'react'
import injectSheet from 'react-jss'
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter
} from 'pobble-components/dist/modal'
import Button from 'pobble-components/dist/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane'
import FormLabel from 'pobble-components/dist/form-elements/form-label'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import FormTextArea from 'pobble-components/dist/form-elements/form-text-area'
import ToggleSwitch from 'pobble-components/dist/form-elements/toggle-switch'
import { errorHandler } from 'pobble-components/dist/error-handler'
import classNames from 'classnames'
import { Formik } from 'formik'
import * as Yup from 'yup'
import DateTimePicker from 'react-datetime-picker'
import { apiCreatePASession } from '../../../../common/api/pupil-activities'
import { withRouter } from 'react-router-dom'
import duck from '../../../../common/images/duck.gif'
import PupilSelectorModal from 'pobble-components/dist/pupil-selector'
import {
  useFeatureCounter,
  SubsCrown
} from 'pobble-components/dist/subscriptions'

const SendToPupilsContext = createContext(null)

const formRowStyles = {
  container: {
    display: 'flex',
    alignItems: 'flex-end',
    marginTop: '20px',
    '&:first-child': {
      marginTop: '0'
    }
  }
}

const FormRow = injectSheet(formRowStyles)(({ classes, children }) => (
  <div className={classes.container}>{children}</div>
))

const formCellStyles = {
  container: {
    minWidth: '200px',
    marginLeft: '20px',
    '&:first-child': {
      marginLeft: '0'
    }
  }
}

const FormCell = injectSheet(formCellStyles)(
  ({ classes, children, className }) => (
    <div className={classNames(classes.container, className)}>{children}</div>
  )
)

const thanksStyles = ({ COLORS }) => ({
  modalContent: {
    minWidth: '400px',
    padding: '0'
  },
  topBox: {
    background: '#fff',
    borderRadius: '8px 8px 0 0',
    textAlign: 'center',
    '& img': {
      marginTop: '10px',
      width: '50%'
    }
  },
  textBox: {
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '20px 20px 25px',
    '& h2': {
      margin: '0 0 .5em',
      fontSize: '1.71em',
      lineHeight: '1.2'
    },
    '& p': {
      fontSize: '1em',
      margin: '0 0 1.2em'
    }
  }
})

const Thanks = injectSheet(thanksStyles)(({ classes }) => {
  const { requestClose } = useContext(SendToPupilsContext)
  return (
    <>
      <ModalContent legacy className={classes.modalContent}>
        <div className={classes.topBox}>
          <img src={duck} alt="Duck" />
        </div>
        <div className={classes.textBox}>
          <h2>Thank you!</h2>
          <p>Your lesson has been sent to your class.</p>
          <Button action={requestClose} tall>
            Okay, thanks
          </Button>
        </div>
      </ModalContent>
    </>
  )
})

const formStyles = ({ COLORS, FONT_SIZES }) => ({
  groupsBone: {
    height: '40px',
    borderRadius: '4px',
    display: 'block'
  },
  groupsCell: {
    minWidth: '200px'
  },
  nameCell: {
    width: '100%'
  },
  modalContent: {
    minWidth: '400px'
  },
  selectPupils: {
    ...FONT_SIZES.HEADING_TINY.STYLE,
    color: COLORS.TEXT.MID_GREY.NORMAL,
    margin: '1em 0',
    textDecoration: 'underline'
  },
  newGroupCell: {
    alignSelf: 'flex-start',
    marginTop: '1.3em'
  },
  dateTimePicker: {
    width: '100%',
    '& .react-datetime-picker__wrapper': {
      borderRadius: '4px',
      borderColor: '#ddd',
      color: COLORS.TEXT.LIGHT_GREY.NORMAL,
      '& input': {
        color: COLORS.TEXT.LIGHT_GREY.NORMAL
      }
    },
    '& .react-datetime-picker__inputGroup': {
      padding: '10px 15px'
    }
  },
  peerReviewSection: {
    margin: '20px -20px 0',
    padding: '20px 20px 0',
    borderTop: '1px solid #ececec'
  },
  peerReviewHint: {
    color: COLORS.TEXT.LIGHT_GREY.NORMAL,
    ...FONT_SIZES.BODY_SMALL.STYLE,
    margin: '16px 0 0'
  },
  peerReviewLabel: {
    marginBottom: '16px !important'
  }
})

const SendToPupilsForm = withRouter(
  injectSheet(formStyles)(
    ({ classes, match, onPupilsSelect, presentationId }) => {
      const {
        setStatus,
        status,
        presentationTitle,
        presentationLo,
        requestClose,
        selectedPupils
      } = useContext(SendToPupilsContext)

      const [sendToPupilsCounter, sendToPupilsSP] = useFeatureCounter(
        'PresentationToPupilSending',
        sp => sp.lesson_planning.send_presentation_to_pupils
      )

      return (
        <Formik
          isInitialValid={true}
          onSubmit={values => {
            if (sendToPupilsSP.restricted) {
              sendToPupilsSP.showDialog({
                trigger: 'send-to-pupils',
                title:
                  'You need a subscription to send unlimited resources to pupils'
              })
              return requestClose()
            }
            setStatus('pending')
            apiCreatePASession({
              title: values.name,
              description: values.description,
              account_ids: selectedPupils,
              accessible_at: values.date,
              presentation_id: presentationId,
              peer_review: selectedPupils.length < 2 ? false : values.peerReview
            })
              .then(() => {
                setStatus('sent')
                sendToPupilsCounter()
              })
              .catch(errorHandler)
          }}
          initialValues={{
            name: presentationTitle,
            date: new Date(),
            description: presentationLo,
            peerReview: false
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Name can not be blank')
          })}>
          {({
            handleSubmit,
            isValid,
            values,
            submitForm,
            handleChange,
            handleBlur,
            errors,
            touched,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit} noValidate>
              <ModalContent legacy className={classes.modalContent}>
                <FormRow>
                  <FormCell className={classes.nameCell}>
                    <FormLabel small>Name</FormLabel>
                    <FormInput
                      name="name"
                      validationError={touched.name && errors.name}
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </FormCell>
                </FormRow>
                <FormRow>
                  <FormCell className={classes.nameCell}>
                    <FormLabel small>Description</FormLabel>
                    <FormTextArea
                      name="description"
                      value={values.description}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </FormCell>
                </FormRow>
                <FormRow>
                  <FormCell className={classes.groupsCell}>
                    <FormLabel small>Share with</FormLabel>
                    {!selectedPupils.length && (
                      <Button tall action={onPupilsSelect}>
                        Select pupils
                      </Button>
                    )}
                    {!!selectedPupils.length && (
                      <Button secondary tall action={onPupilsSelect}>
                        {selectedPupils.length} pupils selected
                      </Button>
                    )}
                  </FormCell>
                </FormRow>
                <FormRow>
                  <FormCell>
                    <FormLabel small>
                      Pupils can access the lesson from:
                    </FormLabel>
                    <DateTimePicker
                      minDate={new Date()}
                      className={classes.dateTimePicker}
                      clearIcon={null}
                      calendarIcon={null}
                      disableClock={true}
                      onChange={value => setFieldValue('date', value)}
                      value={values.date}
                    />
                  </FormCell>
                </FormRow>
                {selectedPupils.length > 1 && (
                  <div className={classes.peerReviewSection}>
                    <FormLabel small className={classes.peerReviewLabel}>
                      Enable peer review?
                    </FormLabel>
                    <ToggleSwitch
                      name="peerReview"
                      checked={values.peerReview}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    <p className={classes.peerReviewHint}>
                      Each pupil will automatically have their work shared with
                      another pupil for them to review
                    </p>
                  </div>
                )}
              </ModalContent>
              <ModalFooter alignRight childrenGap>
                <Button tall simple action={requestClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  pending={status === 'pending'}
                  disabled={
                    !sendToPupilsSP.restricted &&
                    (!isValid || !selectedPupils.length)
                  }
                  tall
                  icon={
                    sendToPupilsSP.restricted ? (
                      <SubsCrown accent />
                    ) : (
                      <FontAwesomeIcon icon={faPaperPlane} />
                    )
                  }>
                  {status === 'pending' && 'Sending'}
                  {status !== 'pending' && 'Share with pupils'}
                </Button>
              </ModalFooter>
            </form>
          )}
        </Formik>
      )
    }
  )
)

const SendToPupilsModal = ({
  isOpen,
  requestClose,
  presentationTitle,
  presentationLo,
  presentationId
}) => {
  const [status, setStatus] = useState(false)
  const [pupilSelectorOpened, setPupilsSelectorOpened] = useState(false)
  const [selectedPupils, setSelectedPupils] = useState([])

  const selectedMap = useMemo(
    () => {
      const result = {}
      selectedPupils.forEach(id => (result[id] = true))
      return result
    },
    [selectedPupils]
  )

  const value = useMemo(
    () => ({
      status,
      setStatus,
      presentationTitle,
      requestClose,
      presentationLo,
      selectedPupils
    }),
    [
      setStatus,
      presentationTitle,
      presentationLo,
      requestClose,
      status,
      selectedPupils
    ]
  )
  return (
    <>
      <Modal
        onAfterClose={() => setStatus(false)}
        isOpen={isOpen}
        requestClose={requestClose}
        closeButton={status !== 'sent'}>
        <SendToPupilsContext.Provider value={value}>
          {status !== 'sent' && <ModalHeader>Share with pupils</ModalHeader>}
          {status !== 'sent' && (
            <SendToPupilsForm
              requestClose={requestClose}
              presentationId={presentationId}
              onPupilsSelect={() => setPupilsSelectorOpened(true)}
            />
          )}
          {status === 'sent' && <Thanks />}
        </SendToPupilsContext.Provider>
        <PupilSelectorModal
          preSelected={selectedMap}
          onSelect={ids => setSelectedPupils(ids)}
          isOpen={pupilSelectorOpened}
          requestClose={() => setPupilsSelectorOpened(false)}
        />
      </Modal>
    </>
  )
}

export default SendToPupilsModal
